import { isChunkLoadError, recoverFromChunkLoadError } from '@shared/utils/appUpdate';
import { useEffect } from 'react';

/**
 * Hook to handle dynamic import failures caused by outdated chunks during deployment.
 * When a chunk fails to load, it triggers a service worker update and page reload.
 */
export const useChunkErrorHandler = () => {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error ?? event.message)) {
        console.error('Chunk loading error detected:', event.message);
        event.preventDefault();
        recoverFromChunkLoadError();
      }
    };

    // Listen for runtime errors
    window.addEventListener('error', handleError);

    // Also handle unhandledrejection for promise-based dynamic imports
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        console.error('Chunk loading error detected (promise rejection):', event.reason);
        event.preventDefault();
        recoverFromChunkLoadError();
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
};
